import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ExternalLink, BookOpen, Wrench } from 'lucide-react';
import { Section, Eyebrow, H2, Reveal, FONT, MONO } from './ui';

const RETURN_KEY = 'vf_return_to';

const ToolLink = ({ tool, children, className, testid, back }) => (tool.url.startsWith('/')
  ? <Link to={tool.url} onClick={() => { sessionStorage.setItem(RETURN_KEY, JSON.stringify({ to: `${window.location.pathname}#resources`, label: back })); window.scrollTo(0, 0); }} className={className} data-testid={testid}>{children}</Link>
  : <a href={tool.url} target="_blank" rel="noopener noreferrer" className={className} data-testid={testid}>{children}</a>);

export const References = ({ d, L, t }) => {
  const [q, setQ] = useState('');
  const [group, setGroup] = useState('all');
  const groups = [['all', t.all], ['seminar', t.gSeminar], ['method', t.gMethod], ['background', t.gBackground]];
  const refs = useMemo(() => {
    const s = q.trim().toLowerCase();
    return d.references.filter((r) => (group === 'all' || r.group === group) && (!s || `${r.authors} ${r.year} ${r.title} ${r.source}`.toLowerCase().includes(s)));
  }, [d.references, q, group]);
  return (
    <Section id="resources">
      <Reveal><Eyebrow>{t.furtherReading}</Eyebrow><H2 className="mt-4">{t.resourcesTitle}</H2><p className="mt-4 text-slate-600 max-w-3xl">{t.resourcesSub}</p></Reveal>
      <div className="mt-10 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.search} className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-300 focus:border-blue-500 focus:outline-none" data-testid="bibliography-search-input" />
        </div>
        <div className="flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1" data-testid="bibliography-category-filter">
          {groups.map(([k, l]) => <button key={k} onClick={() => setGroup(k)} data-testid={`bibliography-filter-${k}`} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${group === k ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>{l}</button>)}
        </div>
        <span className="text-sm text-slate-500 md:ml-auto" data-testid="bibliography-count">{refs.length} / {d.references.length}</span>
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-3" data-testid="bibliography-list">
        {refs.map((r) => (
          <div key={r.title} className="group rounded-2xl border border-slate-200 p-5 hover:border-blue-400 hover:shadow-md transition-[box-shadow,border-color] flex gap-4" data-testid="bibliography-reference-item">
            <BookOpen className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-500"><span className="font-semibold text-slate-800">{r.authors}</span> ({r.year})</p>
              <p className="font-bold text-slate-900 mt-0.5">{r.title}</p>
              <p className="text-sm italic text-slate-500">{r.source}</p>
            </div>
            {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="self-start text-blue-600 hover:text-blue-800" aria-label="DOI" data-testid="bibliography-doi-link"><ExternalLink className="w-4 h-4" /></a>}
          </div>
        ))}
        {!refs.length && <p className="text-slate-500 text-sm" data-testid="bibliography-empty">{t.noResults}</p>}
      </div>
      <Reveal>
        <div className="mt-16 flex items-center gap-3"><Wrench className="w-5 h-5 text-blue-600" /><p className="text-xl font-extrabold" style={FONT}>{t.tools}</p></div>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {d.tools.map((x) => (
            <ToolLink key={x.id} tool={x} back={t.backToResources} testid={`tool-${x.id}-card`} className="group rounded-2xl bg-[#060d24] text-white p-5 hover:-translate-y-1 hover:bg-blue-700 transition-[transform,background-color] duration-300">
              <div className="flex items-start justify-between gap-2"><p className="font-bold">{x.name}</p><ExternalLink className="w-4 h-4 text-blue-300 group-hover:text-white" /></div>
              <p className="mt-2 text-xs text-blue-100/70">{L(x.desc)}</p>
              <p className="mt-3 text-[10px] text-blue-300/70 truncate" style={MONO}>{x.url.replace(/^https?:\/\//, '')}</p>
            </ToolLink>
          ))}
        </div>
      </Reveal>
    </Section>
  );
};
